import bbox from '@turf/bbox'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'
import { MetricEventType } from 'features/metrics/api/metrics'
import { MAXIMUM_ZOOM_LEVEL, MINIMUN_ZOOM_LEVEL } from 'features/common/ui/Map/ZoomControl'
import { clamp } from 'features/common/utils/clamp'
import { parseSync } from '@loaders.gl/core'
import { WKTLoader } from '@loaders.gl/wkt'
import { stringify, GeoJSONGeometry } from 'wellknown'
import cleanCoords from '@turf/clean-coords'
import truncateCoords from '@turf/truncate'
import { GeocoderResult } from 'features/builder/ui/BuilderAddressSearch/BuilderGeocoderWidget'
import { MapConfig } from 'features/maps/types'
import { BuilderSearchParam } from 'features/common/utils/searchParamsUtils'

export function safeParseWKT(wkt: string | null): GeoJSON.Geometry | null {
  if (!wkt) return null

  try {
    return parseSync(wkt, WKTLoader) || null
  } catch (error) {
    console.error('safeParseWKT', error)
  }

  return null
}

export function safeWriteWKT(geometry: GeoJSON.Geometry, precision = 6): string {
  try {
    return stringify(truncateCoords(cleanCoords(geometry), { precision, coordinates: 2 }) as GeoJSONGeometry)
  } catch (error) {
    console.error('safeWriteWKT', error)
  }
  return ''
}

const isLatitude = (lat) => isFinite(lat) && Math.abs(lat) <= 90
const isLongitude = (lng) => isFinite(lng) && Math.abs(lng) <= 180

// Note: RegExp and validation must match @carto/react-widgets `validateCoordinates.js`.
export function safeParseLatLng(text: string): GeocoderResult | null {
  const regexp = /^-?([1-8]?\d(\.\d{1,16})?|90(\.0{1,16})?)(,?\s?)-?((1?[0-7]|\d)?\d(\.\d{1,16})?|180(\.0{1,16})?)$/
  if (!regexp.test(text)) return null

  const [latitude, longitude] = text.split(/[, ]+/).map(parseFloat)
  if (!isLatitude(latitude) || !isLongitude(longitude)) return null

  return { latitude, longitude }
}

const isParamSet = (value: unknown): true | undefined => value !== undefined || undefined

export function getDefaultMapConfigFromSearchParams(searchParams: URLSearchParams, mapId: string): MapConfig {
  const lat = searchParams.get(BuilderSearchParam.LATITUDE)
  const lng = searchParams.get(BuilderSearchParam.LONGITUDE)
  const bearing = searchParams.get(BuilderSearchParam.BEARING)
  const pitch = searchParams.get(BuilderSearchParam.PITCH)
  const zoom = searchParams.get(BuilderSearchParam.ZOOM)
  const layers = searchParams
    .get(BuilderSearchParam.LAYERS)
    ?.split(',')
    .map((l) => parseInt(l))
  const filters = safeParseParams(searchParams.entries(), /^widget_/)
  const spatialFilter = safeParseWKT(searchParams.get(BuilderSearchParam.SPATIAL_FILTER))
  const search = searchParams.get('search')
  const sqlParameters = safeParseParams(searchParams.entries(), /^param_/)

  const params = {
    layers,
    zoom: zoom ? clamp(parseInt(zoom), MINIMUN_ZOOM_LEVEL, MAXIMUM_ZOOM_LEVEL) : undefined,
    lat: lat && !isNaN(lat as never) ? clamp(parseFloat(lat), -90, 90) : undefined,
    lng: lng && !isNaN(lng as never) ? clamp(parseFloat(lng), -180, 180) : undefined,
    bearing: bearing && !isNaN(bearing as never) ? clamp(parseFloat(bearing), 0, 360) : undefined,
    pitch: pitch && !isNaN(pitch as never) ? clamp(parseFloat(pitch), 0, 60) : undefined,
    filters,
    spatialFilter: spatialFilter && isValidSpatialFilter(spatialFilter) ? { geometry: spatialFilter } : undefined,
    search: search || undefined,
    sqlParameters
  }

  if (Object.values(params).some(isParamSet)) {
    sendMetricEvent(MetricEventType.BuilderURLParameterUsed, {
      mapId,
      urlParameters: {
        latlon: isParamSet(params.lat),
        layers: isParamSet(params.layers),
        zoom: isParamSet(params.zoom),
        bearing: isParamSet(params.bearing),
        pitch: isParamSet(params.pitch),
        filters: isParamSet(params.filters),
        parameters: isParamSet(params.sqlParameters),
        search: isParamSet(params.search),
        mask: isParamSet(params.spatialFilter)
      }
    })
  }

  return params
}

function isValidSpatialFilter(geometry: GeoJSON.Geometry): geometry is GeoJSON.Polygon | GeoJSON.MultiPolygon {
  if (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon') return false

  const bounds = bbox(geometry) // [ minX, minY, maxX, maxY ]
  return bounds.every(isFinite) && bounds[2] - bounds[0] > 0 && bounds[3] - bounds[1] > 0
}

function safeParseParams(params: Iterable<[string, string]>, prefix: RegExp): Record<string, string[]> | undefined {
  const results = {} as Record<string, string[]>

  let paramCount = 0

  for (const [paramName, value] of params) {
    if (!prefix.test(paramName) || typeof value !== 'string') {
      continue
    }

    const name = paramName.replace(prefix, '')
    results[name] = value.split(',')
    paramCount++
  }

  return paramCount > 0 ? results : undefined
}
